import { useEffect } from 'react'
import { baseCampApi } from '@api/endpoints/baseCamp'
import { useAuthStore } from '@store/authStore'

/**
 * Tells the server the browser's IANA time zone, so a streak day ends at the user's midnight even
 * when an achievement is awarded by a server-side trigger with no client offset (Backlog 046 §5.2).
 *
 * PUTs only when the zone differs from the last one reported for this user in this browser, and
 * re-checks when the tab becomes visible (someone who travelled). Silent on failure: a failed
 * report is retried on the next check; a 400 (a zone the server does not know) is remembered so it
 * is not re-sent on every focus.
 */

const STORAGE_KEY = 'baseCamp.reportedTimeZone'

function browserZone(): string | null {
  try {
    return Intl.DateTimeFormat().resolvedOptions().timeZone || null
  } catch {
    return null
  }
}

function readStamp(): string | null {
  try {
    return localStorage.getItem(STORAGE_KEY)
  } catch {
    return null
  }
}

function writeStamp(stamp: string) {
  try {
    localStorage.setItem(STORAGE_KEY, stamp)
  } catch {
    // Private window or blocked storage: the worst case is one PUT per page load.
  }
}

let inFlight: string | null = null

export function useTimeZoneReporting() {
  const isAuthenticated = useAuthStore((s) => s.isAuthenticated)
  const userId = useAuthStore((s) => s.user?.userId ?? s.user?.id ?? null)

  useEffect(() => {
    if (!isAuthenticated || !userId) return

    const report = () => {
      const zone = browserZone()
      if (!zone) return
      const stamp = `${userId}|${zone}`
      if (readStamp() === stamp || inFlight === stamp) return
      inFlight = stamp
      baseCampApi
        .setTimeZone(zone)
        .then(() => writeStamp(stamp))
        .catch((err: { response?: { status?: number } }) => {
          if (err?.response?.status === 400) writeStamp(stamp)
        })
        .finally(() => {
          if (inFlight === stamp) inFlight = null
        })
    }

    report()
    const onVisible = () => {
      if (document.visibilityState === 'visible') report()
    }
    document.addEventListener('visibilitychange', onVisible)
    return () => document.removeEventListener('visibilitychange', onVisible)
  }, [isAuthenticated, userId])
}
